import {Component, computed, inject, input, viewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {Divider} from 'primeng/divider';
import {TimesheetService, TimesheetSummary} from '../../../../../api';
import {QueryKey} from '../../../../constants/query-key';
import {TimesheetStoreService} from '../../../timesheets/services/timesheet-store.service';
import {TimesheetForm} from '../../../timesheets/components/timesheet-form/timesheet-form';
import {TimesheetFormValue} from '../../../timesheets/models/timesheet-form-value';
import {mapFormValueToTimesheetRequest} from '../../../timesheets/mappers/timesheet-form.mapper';
import {DurationPipe} from '../../../timesheets/pipes/duration.pipe';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Loader} from '../../../../components/loader/loader';
import {PartyAvatar} from '../../../../components/avatar/party-avatar.component';
import {ElciaIcon} from '../../../../components/icon/icon';
import {ConfirmService} from '../../../../services/confirm.service';

@Component({
  selector: 'elcia-todo-timesheet-list',
  imports: [DatePipe, TranslatePipe, Divider, DurationPipe, ElciaDialog, Loader, PartyAvatar, ElciaIcon, TimesheetForm],
  // Own instance: the timesheets page holds the root one, and both track their own selection.
  providers: [TimesheetStoreService],
  templateUrl: './todo-timesheet-list.html',
  styleUrl: './todo-timesheet-list.scss',
})
export class TodoTimesheetList {
  private readonly timesheetService = inject(TimesheetService);
  private readonly store = inject(TimesheetStoreService);
  private readonly confirmService = inject(ConfirmService);

  todoIds = input.required<number[]>();

  private timesheetsQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEETS, {todoIds: this.todoIds()}],
    queryFn: () => lastValueFrom(this.timesheetService.getTimesheets([], [], [], [], this.todoIds())),
    enabled: this.todoIds().length > 0,
  }));

  timesheets = computed<TimesheetSummary[]>(() => this.timesheetsQuery.data()?.timesheets ?? []);
  isLoading = computed(() => this.timesheetsQuery.isLoading());
  totalDurationInSeconds = computed(() =>
    this.timesheets().reduce((sum, t) => sum + (t.durationInSeconds ?? 0), 0)
  );

  protected timesheetForm = viewChild<TimesheetForm>(TimesheetForm);
  protected isFormDisplayed = computed(() => this.store.selectedTimesheetId() !== null);
  protected selectedTimesheet = computed(() => this.store.selectedTimesheet() ?? undefined);
  protected isLoadingSelectedTimesheet = this.store.isLoadingSelectedTimesheet;
  protected selectedTimesheetDocuments = this.store.selectedTimesheetDocuments;
  protected isFormSaving = computed(() =>
    this.store.updateTimesheetMutation.isPending()
    || this.store.attachTimesheetDocumentMutation.isPending()
    || this.store.deleteTimesheetDocumentMutation.isPending());

  protected openTimesheet(timesheet: TimesheetSummary): void {
    this.store.selectedTimesheetId.set(timesheet.id);
  }

  protected closeForm(): void {
    this.timesheetForm()?.resetPendingDocuments();
    this.store.selectedTimesheetId.set(null);
  }

  protected onDialogSave(): void {
    this.timesheetForm()?.submit();
  }

  protected async submit(formValue: TimesheetFormValue): Promise<void> {
    const form = this.timesheetForm();
    const timesheetId = formValue.id!;

    await this.store.updateTimesheetMutation.mutateAsync({
      id: timesheetId,
      body: mapFormValueToTimesheetRequest(formValue)
    });

    await this.store.saveTimesheetDocuments({
      timesheetId,
      pendingFiles: form?.pendingFiles() ?? [],
      pendingDeletionIds: form?.pendingDeletionIds() ?? [],
    });

    this.closeForm();
  }

  protected deleteTimesheet(): void {
    const timesheet = this.selectedTimesheet();
    if (!timesheet?.id) return;
    this.confirmService.confirm({
      title: 'entity.timesheet',
      message: timesheet.subject ?? '',
      accept: async () => {
        await this.store.deleteTimesheetMutation.mutateAsync(timesheet.id);
        this.closeForm();
      }
    });
  }
}
